"use client";
import { AlertTriangle, Check, ShieldCheck } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  listLanguageVersionOptions,
  type PlaygroundEngineId,
  type PlaygroundVersionOption,
} from "../config/backend-language-contract.generated";
import type { RuntimeCatalogue } from "../types";
import { cn } from "@/lib/utils";

type RuntimeSettingsPanelProps = {
  languageId: string;
  /** Which engine's rows to render. The contract holds both (`EX-PUB04B`). */
  engine: PlaygroundEngineId;
  languageVersion: string;
  onLanguageVersionChange: (versionId: string) => void;
  allowedCompilerOptions: readonly string[];
  selectedCompilerOptions: readonly string[];
  onCompilerOptionsChange: (next: string[]) => void;
  /** Absent while loading or when the catalogue endpoint failed. */
  catalogue?: RuntimeCatalogue;
  className?: string;
};

/**
 * Runtime version and compiler flags — `PG-IDE01`, `docs/24` §3.1, §3.4.
 *
 * Two dimensions, deliberately separated. A **version** is an id the server
 * validates and substitutes a flag for; a **flag** is an exact token from the
 * server's allowlist. `-std=` appears in neither list here, because it is the
 * version — that is what stops a picker saying C++17 while a checkbox compiles
 * C++20.
 *
 * The list renders from the bundled contract, so it is present whether or not
 * discovery answered. Discovery only adds the confirmation line underneath.
 *
 * **`EX-PUB04B` added the engine to that.** The contract now holds both engines'
 * rows, so the panel has to be told which half is live. The compiler-flag block
 * then disappears on the runner without a branch for it: the runner's allowlist
 * is empty for every language, because its own language table owns the command
 * line and its compile request has no field an option could go in.
 */
export function RuntimeSettingsPanel({
  languageId,
  engine,
  languageVersion,
  onLanguageVersionChange,
  allowedCompilerOptions,
  selectedCompilerOptions,
  onCompilerOptionsChange,
  catalogue,
  className,
}: RuntimeSettingsPanelProps) {
  const t = useTranslations("playground");
  // One engine's rows, with that engine's toolchain strings. A version id is
  // shared vocabulary between the two; the compiler behind it is not.
  const versions: readonly PlaygroundVersionOption[] = listLanguageVersionOptions(
    languageId,
    engine,
  );
  const active = versions.find((version) => version.id === languageVersion) ?? versions[0];
  const languageView = catalogue?.languages.find((language) => language.id === languageId);

  const toggleOption = (token: string) => {
    const next = selectedCompilerOptions.includes(token)
      ? selectedCompilerOptions.filter((value) => value !== token)
      : [...selectedCompilerOptions, token];
    onCompilerOptionsChange(next);
  };

  return (
    <div className={cn("space-y-3 text-sm text-white/90", className)}>
      <div>
        <div className="mb-1 text-xs text-white/55">{t("runtimeVersionLabel")}</div>
        {versions.length > 1 ? (
          <div className="space-y-1">
            {versions.map((version) => (
              <button
                key={version.id}
                type="button"
                onClick={() => onLanguageVersionChange(version.id)}
                aria-pressed={version.id === active?.id}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition",
                  version.id === active?.id
                    ? "border-[#E94D1D]/60 bg-[#E94D1D]/15 text-white"
                    : "border-white/15 bg-white/5 text-white/70 hover:bg-white/10",
                )}
              >
                <span className="w-4 shrink-0">
                  {version.id === active?.id ? (
                    <Check className="h-4 w-4 text-[#E94D1D]" aria-hidden />
                  ) : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{version.label}</span>
                  <span className="block truncate text-[11px] text-white/45">
                    {version.toolchain}
                  </span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-white/70">
            <span className="block font-medium text-white/85">{active?.label}</span>
            <span className="block text-[11px] text-white/45">{active?.toolchain}</span>
          </p>
        )}
        <RuntimeVerificationNote catalogue={catalogue} languageId={languageId} />
      </div>

      {allowedCompilerOptions.length > 0 ? (
        <div className="border-t border-white/10 pt-3">
          <div className="mb-1 text-xs text-white/55">{t("compilerOptionsLabel")}</div>
          <div className="flex flex-wrap gap-1.5">
            {allowedCompilerOptions.map((token) => (
              <button
                key={token}
                type="button"
                onClick={() => toggleOption(token)}
                aria-pressed={selectedCompilerOptions.includes(token)}
                className={cn(
                  "rounded-lg border px-2 py-1 font-mono text-[11px] transition",
                  selectedCompilerOptions.includes(token)
                    ? "border-[#E94D1D]/60 bg-[#E94D1D]/15 text-white"
                    : "border-white/15 bg-white/5 text-white/70 hover:bg-white/10",
                )}
              >
                {token}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-white/45">
            {t("compilerOptionsHint")}
          </p>
        </div>
      ) : null}

      {languageView?.verification === "mismatch" ? (
        <p className="flex items-start gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/10 px-2 py-1.5 text-[11px] leading-snug text-amber-200">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {t("runtimeMismatch", { reported: languageView.reportedToolchain ?? "" })}
          </span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Whether anything has confirmed the declared toolchain lately.
 *
 * Deliberately quiet. An unverified runtime is not an error — the catalogue is
 * code and the program still runs — so it reads as an absence of confirmation
 * rather than as a fault.
 */
function RuntimeVerificationNote({
  catalogue,
  languageId,
}: {
  catalogue?: RuntimeCatalogue;
  languageId: string;
}) {
  const t = useTranslations("playground");
  const languageView = catalogue?.languages.find((language) => language.id === languageId);
  if (!catalogue || !languageView) {
    return <p className="mt-1.5 text-[11px] text-white/40">{t("runtimeUnverified")}</p>;
  }
  if (languageView.verification === "verified") {
    return (
      <p className="mt-1.5 flex items-center gap-1 text-[11px] text-white/45">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400/70" aria-hidden />
        <span>
          {catalogue.discovery.state === "stale"
            ? t("runtimeVerifiedStale")
            : t("runtimeVerified")}
        </span>
      </p>
    );
  }
  return <p className="mt-1.5 text-[11px] text-white/40">{t("runtimeUnverified")}</p>;
}
